<template>
  <div class="booking-popup" v-if="booking && bookingStationTime" :class="darkMode ? 'dark' : ''">
    <div class="maincontent">
      <BookingDetailLeftSide :isNetworkAvailable="isNetworkAvailable" :booking="booking" :stations="stations" :experiences="experiences" :locationId="locationId" />
      <BookingDetailRightSide :isNetworkAvailable="isNetworkAvailable" :booking="booking" :locationId="locationId" :isOrganizationEducation="isOrganizationEducation" @toggleMarkAsPaid="toggleMarkAsPaid"  />
      <div class="payment-status-link">
        <p v-for="transaction in booking.transactions" :key="transaction.id" class="f5">Check payment status in the payment provider <a :href="transaction.paymentStatusLink" target="_blank">Dashboard</a></p>
      </div>
    </div>

    <div class="bottombar">
      <BookingDetailCheckin
        v-if="isNetworkAvailable && !booking.checkedInAt && !ended"
        @checkIn="checkIn"
        @editBooking="goToFullBooking" />
      <BookingDetailNotStarted
        v-if="isNetworkAvailable && booking.checkedInAt && !bookingStationTime.startedAt && !ended"
        :startTime="booking.startTime"
        :length="length"
        :currentTime="currentTime"
        :endTime="bookingStationTime.endTime"
        @start="start"
        @editBooking="goToFullBooking" />
      <BookingDetailTimerPaused
        v-if="isNetworkAvailable && booking.checkedInAt && bookingStationTime.startedAt && bookingStationTime.pausedAt && !ended"
        :endTime="bookingStationTime.endTime"
        :currentTime="currentTime"
        :isOrganizationEducation="isOrganizationEducation"
        @openPaidExtension="openPaidExtension"
        @resume="resume"
        @editBooking="goToFullBooking" />
      <BookingDetailTimerRunning
        v-if="isNetworkAvailable && booking.checkedInAt && bookingStationTime.startedAt && !bookingStationTime.pausedAt && !ended"
        :endTime="bookingStationTime.endTime"
        :currentTime="currentTime"
        :isOrganizationEducation="isOrganizationEducation"
        @openPaidExtension="openPaidExtension"
        @pause="pause"
        @editBooking="goToFullBooking" />
      <BookingDetailEnded
        v-if="isNetworkAvailable && booking.checkedInAt && ended"
        :endTime="bookingStationTime.endTime"
        :isOrganizationEducation="isOrganizationEducation"
        @openPaidExtension="openPaidExtension"
        @editBooking="goToFullBooking" />
      <BookingDetailSafetyMode
        v-if="!isNetworkAvailable && bookingStationTime.startedAt && !ended"
        :endTime="bookingStationTime.endTime"
        :pausedAt="bookingStationTime.pausedAt"
        :currentTime="currentTime"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment-timezone';
import deepmerge from 'deepmerge';

import BookingDetailLeftSide from './BookingDetailLeftSide';
import BookingDetailRightSide from './BookingDetailRightSide';

import BookingDetailCheckin from './bottom-bar/BookingDetailCheckin';
import BookingDetailNotStarted from './bottom-bar/BookingDetailNotStarted';
import BookingDetailTimerRunning from './bottom-bar/BookingDetailTimerRunning';
import BookingDetailTimerPaused from './bottom-bar/BookingDetailTimerPaused';
import BookingDetailEnded from './bottom-bar/BookingDetailEnded';
import BookingDetailSafetyMode from './bottom-bar/BookingDetailSafetyMode';

import conflictDetectorWorker from '@/location/conflict-detector.worker';

const conflictDetector = conflictDetectorWorker();

@Component({
  props: {
    locationId: {
      type: String,
      required: true,
    },
    stationId: {
      type: String,
      required: true,
    },
    bookingId: {
      type: String,
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    BookingDetailCheckin,
    BookingDetailNotStarted,
    BookingDetailTimerRunning,
    BookingDetailTimerPaused,
    BookingDetailLeftSide,
    BookingDetailRightSide,
    BookingDetailEnded,
    BookingDetailSafetyMode,
  },
})
export default class BookingDetailRoute extends Vue {
  get isOrganizationEducation () { return this.$permissions.education; }
  get isNetworkAvailable () { return this.$store.getters.isNetworkAvailable; }
  get currentTime () { return this.$store.getters.currentTime; }

  get booking () {
    const booking = this.$store.getters['entities/Booking'](this.bookingId) || {};
    return deepmerge({}, booking);
  }

  get bookingStationTimeIndex () {
    return this.booking?.bookingStationTimes.findIndex(bookingStationTime => bookingStationTime.station.id === this.stationId);
  }

  get bookingStationTime () {
    return this.booking.bookingStationTimes[this.bookingStationTimeIndex];
  }

  get stations () {
    return this.booking.bookingStationTimes.reduce((stations, bookingStationTime) => {
      const station = this.$store.getters['entities/Station'](bookingStationTime.station.id);
      if (station && !stations.includes(station)) stations.push(station);
      return stations;
    }, []);
  }

  get experiences () {
    return this.booking.bookingStationTimes.reduce((experiences, bookingStationTime) => {
      const experience = this.$store.getters['entities/Experience'](bookingStationTime.experience.id);
      if (experience && !experiences.includes(experience)) experiences.push(experience);
      return experiences;
    }, []);
  }

  get ended () {
    return moment(this.bookingStationTime.endTime).diff(moment(this.currentTime)) < 0 && this.bookingStationTime.startedAt;
  }

  get bookings () {
    return this.$store.getters.locationBookings(this.locationId).map(booking => deepmerge({}, booking));
  }

  get length () {
    return Math.round(moment(this.bookingStationTime.endTime).diff(this.booking.startTime) / 1000 / 60);
  }

  openPaidExtension () {
    this.$store.commit('setOverviewScrollPosition');
    this.$router.push({
      name: 'paid-extension',
      params: {
        locationId: this.locationId,
        stationId: this.stationId,
        bookingId: this.bookingId,
        bookingStationTimeIndex: this.bookingStationTimeIndex,
        darkMode: this.darkMode,
      },
    });
  }

  checkIn () {
    if (moment(this.booking.startTime).format('YYYY-MM-DD') !== moment().format('YYYY-MM-DD')) {
      this.$store.commit('setFlash', {
        message: this.$t('This booking is for a different day. Please change the date and try again.'),
        type: 'error',
      });
    } else {
      this.$store.dispatch('storeBooking', { id: this.booking.id, checkedInAt: this.currentTime });
    }
  }

  toggleMarkAsPaid (paid) {
    let bookingStationTimeChanges = [];
    if (paid) {
      bookingStationTimeChanges = this.booking.bookingStationTimes.map(bookingStationTime => {
        return {
          id: bookingStationTime.id,
          amountDue: bookingStationTime.amountPaid,
          amountPaid: 0,
        };
      });
    } else {
      bookingStationTimeChanges = this.booking.bookingStationTimes.map(bookingStationTime => {
        return {
          id: bookingStationTime.id,
          amountDue: 0,
          amountPaid: bookingStationTime.amountDue,
        };
      });
    }
    bookingStationTimeChanges.forEach(bookingStationTime => {
      this.$store.dispatch('storeBookingStationTime', bookingStationTime);
    });
  }

  async start () {
    let bookingStationTimeChanges = [];

    bookingStationTimeChanges = this.booking.bookingStationTimes.map(bookingStationTime => {
      let endTime = bookingStationTime.endTime;

      const diff = moment(this.booking.startTime).diff(moment(this.currentTime));

      if (diff > 0) { // Means they are early
        endTime = moment(bookingStationTime.endTime).subtract(diff, 'milliseconds').format();
      }

      return { id: bookingStationTime.id, station: { id: bookingStationTime.station.id }, endTime, startedAt: this.currentTime };
    });

    const bookings = await conflictDetector.getBookingsWithUpdatedEndTimesForStarting(
      { id: this.booking.id, bookingStationTimes: bookingStationTimeChanges },
      this.bookings,
    );

    const bookingsWithJustEndTimes = bookings.reduce((bookingsAccumulator, booking) => {
      return [...bookingsAccumulator, {
        id: booking.id,
        bookingStationTimes: booking.bookingStationTimes.map(bookingStationTime => ({
          id: bookingStationTime.id,
          endTime: bookingStationTime.endTime,
        })),
      }];
    }, []);
    bookingsWithJustEndTimes.forEach(booking => this.$store.dispatch('storeBooking', booking));

    bookingStationTimeChanges.forEach(bookingStationTime => {
      this.$store.dispatch('storeBookingStationTime', bookingStationTime);
    });
  }

  pause () {
    const bookingStationTime = this.booking.bookingStationTimes[this.bookingStationTimeIndex];
    const bookingStationTimeChanges = { id: bookingStationTime.id };
    bookingStationTimeChanges.pausedAt = this.currentTime;
    bookingStationTimeChanges.pausedDuration =
      moment(bookingStationTime.endTime).diff(moment(this.currentTime), 'seconds');
    this.$store.dispatch('storeBookingStationTime', bookingStationTimeChanges);
  }

  resume (secondsFromNow) {
    const bookingStationTime = this.booking.bookingStationTimes[this.bookingStationTimeIndex];
    const bookingStationTimeChanges = { id: bookingStationTime.id };
    const newEndTime = moment(this.currentTime).add(secondsFromNow, 'seconds').format();

    bookingStationTimeChanges.pausedAt = null;
    bookingStationTimeChanges.pausedDuration = null;
    bookingStationTimeChanges.endTime = newEndTime;

    this.$store.dispatch('storeBookingStationTime', bookingStationTimeChanges);
  }

  goToFullBooking () {
    this.$store.commit('setOverviewScrollPosition');
    if (this.bookingStationTime.startedAt) {
      this.$router.push({
        name: 'edit-booking-after-start',
        params: {
          bookingId: this.booking.id,
          darkMode: this.darkMode,
        },
      });
    } else {
      this.$router.push({
        name: 'booking',
        params: {
          bookingId: this.booking.id,
          darkMode: this.darkMode,
        },
      });
    }
  }
}
</script>

<style scoped lang="postcss">
@import 'core/shared/styles';

.booking-popup {
  display: grid;
  padding: var(--spacingSm);

  @media (--tablet) {
    padding: 0;
    min-width: 75vw;
    max-width: 90vw;
  }

  @media (--desktop) {
    min-width: 60vw;
  }

  @media (--desktopXL) {
    min-width: 50vw;
  }

  & .maincontent {
    display: grid;

    @media (--tablet) {
      padding: var(--spacingMd);
      grid-template-columns: 50% 50%;
    }
  }

  & .bottombar {
    @media (--tablet) {
      padding: 0;
    }
  }

  .payment-status-link {
    margin-top: 3em;
    grid-column: span 2;
  }
}

.booking-popup.dark {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText);
}

.booking-popup.dark .maincontent .booking-detail-left-side /deep/ .date-and-time .guestlistcount   {
  color: var(--darkModeColorText) !important;
}

.booking-popup.dark .maincontent .booking-detail-left-side /deep/ .bookingstationtimes .bookingstationtime .date {
  color: var(--darkModeColorText) !important;
}

.booking-popup.dark .maincontent .booking-detail-right-side /deep/ .booking-price .location-price .taxrow .taxes {
  color: var(--darkModeColorText) !important;
}

.booking-popup.dark .maincontent .booking-detail-right-side /deep/ .booking-price div .ui-checkbox .box-and-title .title {
  color: var(--darkModeColorText) !important;
}

.booking-popup.dark .bottombar .popup-checkin /deep/ .left .ui-button {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
}

.booking-popup.dark .bottombar .popup-timer-not-started /deep/ .left .ui-button {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
}

.booking-popup.dark .bottombar .popup-timer-running /deep/ .left {
  & .ui-button {
    background-color: var(--darkModeColorDarkBackground);
    color: var(--darkModeColorText) !important;
  }
  & svg {
    fill: var(--darkModeColorText) !important;
  }
}

.booking-popup.dark .bottombar .popup-timer-paused /deep/ .left {
  & .ui-button {
    background-color: var(--darkModeColorDarkBackground);
    color: var(--darkModeColorText) !important;
  }
  & svg {
    fill: var(--darkModeColorText) !important;
  }
}

.booking-popup.dark .bottombar .popup-timer-paused /deep/ .right .inputs div  .ui-input {
    background-color: var(--darkModeColorDarkBackground);
    color: var(--darkModeColorText) !important;
    fill: var(--darkModeColorText) !important;
}
</style>
